import { useEffect } from 'react'
import { type LoaderFunctionArgs, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { BlockRenderer } from '#app/components/block-renderer.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { buildMeta, buildOrganizationJsonLd, buildWebSiteJsonLd } from '#app/utils/meta.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = ({ data }) => {
	const page = data?.page
	const title = page?.metaTitle || 'Ignite Identity - Bureau met een social missie'
	const description = page?.metaDescription || 'Merkstrategie, Merkverhaal, Identiteit, Interne en externe communicatie, campagne. Bureau met een social missie.'
	return [
		...buildMeta({
			title,
			description,
			url: '/',
		}),
		buildOrganizationJsonLd(),
		buildWebSiteJsonLd(),
	]
}

export async function loader({ request: _request }: LoaderFunctionArgs) {
	// Fetch the home page from the database
	const page = await prisma.page.findUnique({
		where: { slug: 'home' },
		include: {
			blocks: {
				orderBy: { order: 'asc' },
			},
		},
	})

	// Fetch services and case studies for dynamic blocks
	const [services, caseStudies] = await Promise.all([
		prisma.service.findMany({
			select: { id: true, name: true, slug: true, description: true },
			orderBy: { name: 'asc' },
		}),
		prisma.caseStudy.findMany({
			where: { published: true },
			include: { services: true },
			orderBy: { order: 'asc' },
			take: 7,
		}),
	])

	return {
		page,
		services,
		caseStudies: caseStudies.map((cs) => ({
			...cs,
			videoMode: (cs.heroVideoMode as 'none' | 'hover' | 'autoplay') ?? 'none',
			videoPlaybackId: cs.heroVideoPlaybackId ?? undefined,
		})),
		ctaConfig: page
			? {
					mode: page.ctaMode,
					title: page.ctaTitle,
					buttonText: page.ctaButtonText,
					buttonLink: page.ctaButtonLink,
					imageUrl: page.ctaImageUrl,
					imageAlt: page.ctaImageAlt,
					highlightWords: page.ctaHighlightWords,
					fontSizePreset: page.ctaFontSizePreset,
					showButton: page.ctaShowButton,
				}
			: null,
	}
}

export default function Index({ loaderData }: Route.ComponentProps) {
	const { page, services, caseStudies } = loaderData
	const [searchParams] = useSearchParams()

	// Handle newsletter confirmation messages
	useEffect(() => {
		const confirmed = searchParams.get('confirmed')
		if (confirmed === 'success') {
			toast.success('Your newsletter subscription has been confirmed!')
		} else if (confirmed === 'already') {
			toast.info('Your email is already confirmed.')
		}
	}, [searchParams])

	// If no page found or no blocks, show a message
	if (!page || !page.blocks || page.blocks.length === 0) {
		return (
			<main className="flex min-h-[50vh] items-center justify-center">
				<p className="text-muted-foreground">
					Home pagina niet geconfigureerd. Ga naar admin om de home pagina in te stellen.
				</p>
			</main>
		)
	}

	return (
		<main>
			<BlockRenderer
				blocks={page.blocks}
				services={services}
				caseStudies={caseStudies}
			/>
		</main>
	)
}
